import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Dialog, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { setOrbitAISnackBar } from 'src/redux/siteAuditReducer';
import { useContentStrategyActions } from '../hooks/useContentStrategyActions';
import {
  ContentStrategyApiResponseV2,
  ContentOpportunityItemV2,
  PagePlanV2
} from '../../types/content-strategy-v2.type';
import './ContentStrategyReport.css';

interface ContentStrategyReportProps {
  data?: ContentStrategyApiResponseV2 | any;
  siteAuditDetails?: any;
  userAgentDetails?: any;
  onIgnoreOpportunity?: (id: string, customEndpoint?: string) => void;
  highlightOpportunity?: { id: string; ts: number } | null;
}

interface GapItem {
  id: string;
  indexLabel: string;
  title: string;
  summary: string;
  severity: 'high' | 'medium' | 'low';
  gapType?: string;
  yourSite: {
    label: string;
    url?: string;
    description?: string;
  };
  competitor: {
    label: string;
    url?: string;
    description?: string;
  };
  whyItMatters?: string;
  response?: string;
  opportunityIds?: string[];
  addressedBy?: string;
}

interface OpportunityItem {
  id: string;
  indexLabel: string;
  type: 'new_page' | 'update' | 'blog';
  typeLabel: string;
  priority:
    | 'Highest priority'
    | 'High priority'
    | 'Medium priority'
    | 'Low priority';
  title: string;
  summary: string;
  gapIds?: string[];
  gapLinks: string[];
  targetUrl?: string;
  blogTopic?: string;
  recommendedChanges?: any[];
  pagePlan?: PagePlanV2 | null;
  action?: {
    type?: string;
    topic?: string;
    endpoint?: string;
  };
  changes?: {
    pageTitle?: string;
    h1?: string;
    openingCopy?: string;
    newSection?: string;
    proofSection?: string;
    servicePromise?: string;
    callsToAction?: string;
  };
  targetKeyword?: string;
  brief?: string;
  rawItem?: ContentOpportunityItemV2 | any;
}

export const ContentStrategyReport: React.FC<ContentStrategyReportProps> = ({
  data,
  siteAuditDetails,
  userAgentDetails,
  highlightOpportunity
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { generatePagePlanMutation } = useContentStrategyActions();

  // Accordion state for Gaps
  const [openGaps, setOpenGaps] = useState<Record<string, boolean>>({});

  // Expanded state for Opportunity Change Panels
  const [openChanges, setOpenChanges] = useState<Record<string, boolean>>({});

  // Generated Plan Cache (stored by opportunity ID)
  const [generatedPlans, setGeneratedPlans] = useState<
    Record<string, PagePlanV2>
  >({});

  // Modal Dialog state for AI Page Plan
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
  const [selectedPlanOpp, setSelectedPlanOpp] =
    useState<OpportunityItem | null>(null);
  const [isPlanLoading, setIsPlanLoading] = useState(false);

  // 1. Determine site domain dynamically
  const siteDomain = useMemo(() => {
    const raw =
      data?.site_url ||
      siteAuditDetails?.domain ||
      siteAuditDetails?.site_url ||
      '';
    return raw.replace(/^https?:\/\//, '').replace(/\/$/, '');
  }, [data?.site_url, siteAuditDetails]);

  // 2. Determine competitors dynamically
  const competitors = useMemo<string[]>(() => {
    const fromData = Array.isArray(data?.competitors) ? data.competitors : [];
    if (fromData.length > 0) return fromData;

    const fromUserAgent =
      userAgentDetails?.response?.orbit_ai?.content_strategy?.competitors;
    if (Array.isArray(fromUserAgent) && fromUserAgent.length > 0) {
      return fromUserAgent;
    }

    const fromSiteAudit = siteAuditDetails?.competitors;
    if (Array.isArray(fromSiteAudit) && fromSiteAudit.length > 0) {
      return fromSiteAudit;
    }

    return [];
  }, [userAgentDetails, data, siteAuditDetails]);

  // 3. Format Last Updated dynamically
  const updatedText = useMemo(() => {
    if (!data?.last_updated) return '';
    try {
      const date = new Date(data.last_updated);
      if (isNaN(date.getTime())) return '';
      const isToday = new Date().toDateString() === date.toDateString();
      const timeStr = date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
      });
      if (isToday) {
        return `Updated today, ${timeStr}`;
      }
      return `Updated ${date.toLocaleDateString([], {
        month: 'short',
        day: 'numeric'
      })}, ${timeStr}`;
    } catch {
      return '';
    }
  }, [data?.last_updated]);

  // 4. Adapt Gaps Data Dynamically from API response
  const rawGaps = useMemo<any[]>(() => {
    return (
      (Array.isArray(data?.content_gaps) && data.content_gaps) ||
      (Array.isArray(data?.data?.content_gaps) && data.data.content_gaps) ||
      (Array.isArray(data?.result?.content_gaps) && data.result.content_gaps) ||
      (Array.isArray(data?.gaps) && data.gaps) ||
      (Array.isArray(data?.existing_content_improvements?.items) &&
        data.existing_content_improvements.items) ||
      (Array.isArray(data?.existing_content_improvements) &&
        data.existing_content_improvements) ||
      []
    );
  }, [data]);

  // Raw Opportunities array
  const rawOpps = useMemo<any[]>(() => {
    return (
      (Array.isArray(data?.content_opportunities) &&
        data.content_opportunities) ||
      (Array.isArray(data?.content_opportunities?.items) &&
        data.content_opportunities.items) ||
      (Array.isArray(data?.data?.content_opportunities?.items) &&
        data.data.content_opportunities.items) ||
      (Array.isArray(data?.data?.content_opportunities) &&
        data.data.content_opportunities) ||
      (Array.isArray(data?.result?.content_opportunities?.items) &&
        data.result.content_opportunities.items) ||
      (Array.isArray(data?.result?.content_opportunities) &&
        data.result.content_opportunities) ||
      (Array.isArray(data?.result?.content_gaps) && data.result.content_gaps) ||
      (Array.isArray(data?.opportunities) && data.opportunities) ||
      []
    );
  }, [data]);

  // Map of Gap ID -> Gap index label (e.g. "legacy_gap_1" -> "G1")
  const gapIdToLabelMap = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    rawGaps.forEach((gap, idx) => {
      const id = gap._id || gap.id || `G${idx + 1}`;
      map[id] = `G${idx + 1}`;
      map[String(idx + 1)] = `G${idx + 1}`;
    });
    return map;
  }, [rawGaps]);

  // Map of Opportunity ID -> Opportunity index label & Title
  const oppIdToInfoMap = useMemo<
    Record<string, { label: string; title: string }>
  >(() => {
    const map: Record<string, { label: string; title: string }> = {};
    rawOpps.forEach((opp, idx) => {
      const id = opp._id || opp.id || `O${idx + 1}`;
      const title =
        opp.title ||
        opp.suggested_title ||
        opp.topic ||
        `Opportunity ${idx + 1}`;
      map[id] = { label: `O${idx + 1}`, title };
      map[String(idx + 1)] = { label: `O${idx + 1}`, title };
    });
    return map;
  }, [rawOpps]);

  // 4. Adapt Gaps Data
  const gapsList = useMemo<GapItem[]>(() => {
    if (!rawGaps.length) return [];

    return rawGaps.map((gap: any, idx: number) => {
      const id = gap._id || gap.id || `G${idx + 1}`;
      const rawSeverity = (
        gap.severity ||
        gap.priority ||
        'medium'
      ).toLowerCase();
      const severity: 'high' | 'medium' | 'low' = [
        'high',
        'medium',
        'low'
      ].includes(rawSeverity)
        ? (rawSeverity as any)
        : 'medium';

      const userPageObj =
        Array.isArray(gap.user_pages) && gap.user_pages.length > 0
          ? gap.user_pages[0]
          : null;
      const userUrl =
        (typeof userPageObj === 'string' ? userPageObj : userPageObj?.url) ||
        gap.your_site?.url ||
        gap.url ||
        (siteDomain ? `${siteDomain}/` : '');

      const userDesc =
        (typeof userPageObj === 'object'
          ? userPageObj?.snippet || userPageObj?.excerpt || userPageObj?.title
          : null) ||
        gap.your_site?.description ||
        gap.your_site?.content ||
        gap.difference ||
        gap.summary ||
        'No page on the site currently targets this keyword or provides a clear response.';

      const compPageObj =
        Array.isArray(gap.competitor_evidence) &&
        gap.competitor_evidence.length > 0
          ? gap.competitor_evidence[0]
          : null;
      const compUrl =
        (typeof compPageObj === 'string' ? compPageObj : compPageObj?.url) ||
        gap.competitor?.url ||
        gap.competitor_url ||
        (Array.isArray(gap.competitors_ranking)
          ? gap.competitors_ranking[0]
          : '') ||
        (competitors.length > 0 ? competitors[idx % competitors.length] : '');

      const compDesc =
        (typeof compPageObj === 'object'
          ? compPageObj?.snippet || compPageObj?.title
          : null) ||
        gap.competitor?.description ||
        gap.competitor?.content ||
        gap.difference ||
        gap.summary ||
        'Competitors provide dedicated, ranking content with structured comparisons and calls to action.';

      // Determine addressedBy reference from opportunity_ids
      let addressedBy = gap.addressed_by || '';
      if (
        !addressedBy &&
        Array.isArray(gap.opportunity_ids) &&
        gap.opportunity_ids.length > 0
      ) {
        const firstOppId = gap.opportunity_ids[0];
        const oppInfo = oppIdToInfoMap[firstOppId];
        if (oppInfo) {
          addressedBy = `Addressed by opportunity ${oppInfo.label} · ${oppInfo.title}`;
        }
      }

      return {
        id,
        indexLabel: `G${idx + 1}`,
        title:
          gap.title || gap.page_title || gap.topic || `Content gap #${idx + 1}`,
        summary: gap.summary || gap.issue || gap.current_issue || '',
        severity,
        gapType: gap.gap_type,
        yourSite: {
          label: gap.your_site?.label || 'Your site',
          url: userUrl,
          description: userDesc
        },
        competitor: {
          label:
            gap.competitor?.label ||
            (idx % 2 === 0 ? 'Competitors do better' : 'Competitor pattern'),
          url: compUrl,
          description: compDesc
        },
        whyItMatters:
          gap.why_it_matters ||
          gap.why_suggested ||
          gap.card_why ||
          gap.summary ||
          '',
        response:
          gap.recommended_response ||
          gap.response ||
          gap.recommended_action ||
          gap.summary ||
          '',
        opportunityIds: gap.opportunity_ids,
        addressedBy
      };
    });
  }, [rawGaps, siteDomain, competitors, oppIdToInfoMap]);

  // 5. Adapt Opportunities Data
  const opportunitiesList = useMemo<OpportunityItem[]>(() => {
    if (!rawOpps.length) return [];

    return rawOpps.map((item: any, idx: number) => {
      const id = item._id || item.id || `O${idx + 1}`;
      const rawType = (item.type || item.content_type || '').toLowerCase();
      const actionType =
        item.action?.type || item.actions?.generate_content?.type;

      let type: 'new_page' | 'update' | 'blog' = 'new_page';
      let typeLabel = 'New page';

      if (
        rawType === 'blog_idea' ||
        rawType === 'blog_post' ||
        rawType === 'blog' ||
        rawType === 'article' ||
        actionType === 'open_blog_agent' ||
        actionType === 'redirectToBlogAgent' ||
        Boolean(item.blog_topic)
      ) {
        type = 'blog';
        typeLabel = 'Blog idea';
      } else if (
        rawType === 'content_change' ||
        rawType === 'update' ||
        rawType === 'improvement' ||
        rawType === 'rewrite' ||
        Boolean(item.recommended_changes?.length) ||
        Boolean(item.recommended_fix) ||
        Boolean(item.changes)
      ) {
        type = 'update';
        typeLabel = 'Content change';
      } else {
        type = 'new_page';
        typeLabel = 'New page';
      }

      const rawPriority = (
        item.priority ||
        item.impact ||
        'high'
      ).toLowerCase();
      let priority:
        | 'Highest priority'
        | 'High priority'
        | 'Medium priority'
        | 'Low priority' = 'High priority';
      if (idx === 0 || item.ai_top_pick || rawPriority === 'highest')
        priority = 'Highest priority';
      else if (rawPriority === 'high') priority = 'High priority';
      else if (rawPriority === 'medium') priority = 'Medium priority';
      else priority = 'Low priority';

      // Build Gap reference links (e.g. ["Closes G1"])
      let gapLinks: string[] = [];
      if (Array.isArray(item.gap_ids) && item.gap_ids.length > 0) {
        gapLinks = item.gap_ids.map((gId: string) => {
          const label = gapIdToLabelMap[gId] || gId;
          return `Closes ${label}`;
        });
      } else if (Array.isArray(item.gap_links) && item.gap_links.length > 0) {
        gapLinks = item.gap_links.map((g: string) =>
          g.startsWith('Closes') ? g : `Closes ${g}`
        );
      } else if (item.closes_gaps) {
        gapLinks = Array.isArray(item.closes_gaps)
          ? item.closes_gaps
          : [item.closes_gaps];
      }

      const title =
        item.title ||
        item.suggested_title ||
        item.topic ||
        item.name ||
        `Opportunity ${idx + 1}`;

      const summary =
        item.summary || item.brief || item.card_why || item.description || '';

      // Structure recommended changes
      const dynamicChanges =
        item.changes ||
        (item.recommended_changes
          ? {
              pageTitle:
                item.recommended_changes?.find(
                  (c: any) => c.label === 'Page title' || c.field === 'title'
                )?.value || title,
              h1:
                item.recommended_changes?.find(
                  (c: any) => c.label === 'H1' || c.field === 'h1'
                )?.value || title,
              openingCopy:
                item.recommended_changes?.find(
                  (c: any) =>
                    c.label === 'Opening copy' || c.field === 'opening_copy'
                )?.value || summary,
              proofSection: item.recommended_changes?.find(
                (c: any) => c.label === 'Proof section' || c.field === 'proof'
              )?.value
            }
          : type === 'update'
          ? {
              pageTitle: `${title} | ${siteDomain}`,
              h1: title,
              openingCopy: summary
            }
          : undefined);

      return {
        id,
        indexLabel: `O${idx + 1}`,
        type,
        typeLabel,
        priority,
        title,
        summary,
        gapIds: item.gap_ids,
        gapLinks,
        targetUrl: item.target_url,
        blogTopic: item.blog_topic || item.action?.topic,
        recommendedChanges: item.recommended_changes,
        pagePlan: item.page_plan || generatedPlans[id] || null,
        action: item.action,
        changes: dynamicChanges,
        targetKeyword: item.target_keyword || item.blog_topic || item.topic,
        brief: item.brief || summary,
        rawItem: item
      };
    });
  }, [rawOpps, gapIdToLabelMap, generatedPlans, siteDomain]);

  // Auto-open first gap item
  useEffect(() => {
    if (gapsList.length > 0) {
      setOpenGaps((prev) => {
        if (Object.keys(prev).length === 0 && gapsList[0]?.id) {
          return { [gapsList[0].id]: true };
        }
        return prev;
      });
    }
  }, [gapsList]);

  // Handle highlighting from search/insight
  useEffect(() => {
    if (highlightOpportunity?.id) {
      const el = document.getElementById(`opp-${highlightOpportunity.id}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [highlightOpportunity]);

  const toggleGap = (id: string) => {
    setOpenGaps((prev) => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const toggleChangePanel = (id: string) => {
    setOpenChanges((prev) => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const handleOpenPlanModal = (opp: OpportunityItem) => {
    setSelectedPlanOpp(opp);
    setIsPlanModalOpen(true);

    const existingPlan =
      opp.pagePlan ||
      generatedPlans[opp.id] ||
      generatedPlans[opp.rawItem?._id || ''];

    if (!existingPlan) {
      setIsPlanLoading(true);
      const oppId = opp.rawItem?._id || opp.rawItem?.id || opp.id;
      const customEndpoint =
        opp.rawItem?.actions?.generate_content?.endpoint ||
        opp.action?.endpoint;

      generatePagePlanMutation.mutate(
        {
          opportunityId: oppId,
          customEndpoint
        },
        {
          onSuccess: (res: any) => {
            const plan =
              res?.page_plan ||
              res?.data?.page_plan ||
              res?.response?.page_plan ||
              res?.plan ||
              res?.data ||
              res;

            if (
              plan &&
              typeof plan === 'object' &&
              (plan.hero || plan.sections || plan.seo || plan.overview)
            ) {
              setGeneratedPlans((prev) => ({
                ...prev,
                [opp.id]: plan,
                [oppId]: plan
              }));
              dispatch(
                setOrbitAISnackBar({
                  status: 'success',
                  message: 'Page plan generated successfully'
                })
              );
            }
          },
          onError: (err: any) => {
            setIsPlanModalOpen(false);
            setSelectedPlanOpp(null);
            dispatch(
              setOrbitAISnackBar({
                status: 'error',
                message:
                  err?.response?.data?.message ||
                  err?.response?.data?.error ||
                  err?.message ||
                  err?.error ||
                  'Failed to generate page plan. Please try again.'
              })
            );
          },
          onSettled: () => {
            setIsPlanLoading(false);
          }
        }
      );
    } else {
      setIsPlanLoading(false);
    }
  };

  /*
  const handleRegeneratePlan = () => {
    if (!selectedPlanOpp) return;
    setIsPlanLoading(true);
    const oppId =
      selectedPlanOpp.rawItem?._id ||
      selectedPlanOpp.rawItem?.id ||
      selectedPlanOpp.id;
    const customEndpoint =
      selectedPlanOpp.rawItem?.actions?.generate_content?.regenerate_endpoint ||
      selectedPlanOpp.rawItem?.actions?.generate_content?.endpoint ||
      selectedPlanOpp.action?.endpoint;

    generatePagePlanMutation.mutate(
      {
        opportunityId: oppId,
        regenerate: true,
        customEndpoint
      },
      {
        onSuccess: (res: any) => {
          const plan =
            res?.page_plan ||
            res?.data?.page_plan ||
            res?.response?.page_plan ||
            res?.plan ||
            res?.data ||
            res;

          if (plan && typeof plan === 'object') {
            setGeneratedPlans((prev) => ({
              ...prev,
              [selectedPlanOpp.id]: plan,
              [oppId]: plan
            }));
            dispatch(
              setOrbitAISnackBar({
                status: 'success',
                message: 'Page plan regenerated successfully'
              })
            );
          }
        },
        onError: (err: any) => {
          dispatch(
            setOrbitAISnackBar({
              status: 'error',
              message:
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                err?.message ||
                err?.error ||
                'Failed to regenerate page plan. Please try again.'
            })
          );
        },
        onSettled: () => {
          setIsPlanLoading(false);
        }
      }
    );
  };
  */

  const handleClosePlanModal = () => {
    setIsPlanModalOpen(false);
    setSelectedPlanOpp(null);
    setIsPlanLoading(false);
  };

  const handleOpenBlogAgent = (topic: string, opp: OpportunityItem) => {
    const blogTopic = opp.blogTopic || topic;
    navigate('/orbit-ai-agent/blog-post-agent', {
      state: {
        prefill: {
          topic: blogTopic,
          target_keyword: opp.targetKeyword || blogTopic,
          suggested_title: opp.title,
          brief: opp.summary || opp.brief
        }
      }
    });
  };

  // Helper for dynamic selected Page Plan from API
  const currentPlan = useMemo<PagePlanV2 | null>(() => {
    if (!selectedPlanOpp) return null;
    return (
      selectedPlanOpp.pagePlan || generatedPlans[selectedPlanOpp.id] || null
    );
  }, [selectedPlanOpp, generatedPlans]);

  // Derived Audience for Overview
  const planAudience = useMemo<string>(() => {
    if (!selectedPlanOpp && !currentPlan) return '-';
    return (
      (currentPlan as any)?.audience ||
      (currentPlan as any)?.target_audience ||
      (currentPlan as any)?.hero?.audience ||
      (selectedPlanOpp as any)?.targetAudience ||
      (selectedPlanOpp as any)?.rawItem?.target_audience ||
      (selectedPlanOpp as any)?.rawItem?.details?.target_audience ||
      (selectedPlanOpp as any)?.rawItem?.audience ||
      '-'
    );
  }, [currentPlan, selectedPlanOpp]);

  // Derived Internal links list for dedicated Internal Links section
  const internalLinksList = useMemo<
    Array<{ anchor: string; target_url: string }>
  >(() => {
    if (!currentPlan) return [];
    const rawLinks =
      currentPlan.internal_links ||
      (currentPlan as any)?.seo?.internal_links ||
      (currentPlan as any)?.links ||
      [];

    if (Array.isArray(rawLinks) && rawLinks.length > 0) {
      return rawLinks
        .map((l: any) => {
          if (typeof l === 'string') {
            return {
              anchor: l,
              target_url: l.startsWith('/')
                ? l
                : `/${l.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`
            };
          }
          if (l && typeof l === 'object') {
            return {
              anchor: l.anchor || l.text || l.title || l.target_url || '-',
              target_url: l.target_url || l.url || l.href || '-'
            };
          }
          return null;
        })
        .filter((item): item is { anchor: string; target_url: string } =>
          Boolean(item)
        );
    }
    return [];
  }, [currentPlan]);

  // Derived Visual Guidance list
  const visualGuidanceList = useMemo<string[]>(() => {
    if (!currentPlan) return [];
    const candidates = [
      (currentPlan as any).visual_guidance,
      (currentPlan as any).visual_notes,
      (currentPlan as any).visual_guidelines,
      (currentPlan as any).visual,
      currentPlan.design_notes,
      currentPlan.hero?.visual_notes,
      (currentPlan as any).hero?.visual_guidance,
      (currentPlan as any).guidelines?.visual,
      (currentPlan as any).guidance?.visual,
      (currentPlan as any).ux_notes
    ];

    for (const cand of candidates) {
      if (!cand) continue;
      if (Array.isArray(cand) && cand.length > 0) {
        const items = cand
          .flatMap((item) => (typeof item === 'string' ? item.split('\n') : []))
          .map((s: string) => s.trim().replace(/^[-•*]\s*/, ''))
          .filter(Boolean);
        if (items.length > 0) return items;
      }
      if (typeof cand === 'string' && cand.trim()) {
        const items = cand
          .split(/\r?\n|•/)
          .map((s: string) => s.trim().replace(/^[-•*]\s*/, ''))
          .filter(Boolean);
        if (items.length > 0) return items;
      }
    }

    return [];
  }, [currentPlan]);

  // Derived Mobile Behaviour list
  const mobileBehaviourList = useMemo<string[]>(() => {
    if (!currentPlan) return [];
    const candidates = [
      (currentPlan as any).mobile_behavior,
      (currentPlan as any).mobile_behaviour,
      (currentPlan as any).mobile_guidance,
      (currentPlan as any).mobile_guidelines,
      (currentPlan as any).mobile_notes,
      (currentPlan as any).mobile,
      (currentPlan as any).hero?.mobile_behavior,
      (currentPlan as any).hero?.mobile_behaviour,
      (currentPlan as any).hero?.mobile_notes,
      (currentPlan as any).guidelines?.mobile,
      (currentPlan as any).guidance?.mobile
    ];

    for (const cand of candidates) {
      if (!cand) continue;
      if (Array.isArray(cand) && cand.length > 0) {
        const items = cand
          .flatMap((item) => (typeof item === 'string' ? item.split('\n') : []))
          .map((s: string) => s.trim().replace(/^[-•*]\s*/, ''))
          .filter(Boolean);
        if (items.length > 0) return items;
      }
      if (typeof cand === 'string' && cand.trim()) {
        const items = cand
          .split(/\r?\n|•/)
          .map((s: string) => s.trim().replace(/^[-•*]\s*/, ''))
          .filter(Boolean);
        if (items.length > 0) return items;
      }
    }

    return [];
  }, [currentPlan]);

  return (
    <div className="cs-report-root">
      <main className="cs-report-main">
        {/* Report Header */}
        <div className="cs-report-header">
          <div>
            <h1>Content Strategy</h1>
            <p className="cs-lede">
              See where competitor content is stronger, then work through the
              most valuable changes and new content for your site.
            </p>
          </div>
          <span className="cs-status">Analysis complete</span>
        </div>

        {/* Run Card */}
        <div className="cs-run-card" aria-label="Analysis details">
          <div className="cs-run-item">
            <div className="cs-eyebrow">Your website</div>
            <div className="cs-run-value">{siteDomain || '—'}</div>
          </div>
          <div className="cs-run-item">
            <div className="cs-eyebrow">Compared with</div>
            <div className="cs-competitors">
              {competitors.length > 0 ? (
                competitors.map((comp, idx) => (
                  <span key={idx} className="cs-chip">
                    {comp.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                  </span>
                ))
              ) : (
                <span className="text-xs text-[#667085] italic">
                  No competitors configured
                </span>
              )}
            </div>
          </div>
          {updatedText && (
            <div className="cs-run-item cs-run-time">{updatedText}</div>
          )}
        </div>

        {/* Section 1: Content Gaps */}
        {gapsList.length > 0 && (
          <section id="gaps" className="cs-section">
            <div className="cs-section-head">
              <div>
                <div className="cs-section-title">
                  <h2>Content Gaps</h2>
                  <span className="cs-count">{gapsList.length}</span>
                </div>
                <p className="cs-section-copy">
                  Where competitor content gives visitors a clearer or more
                  useful answer.
                </p>
              </div>
              <div className="cs-section-note">
                Open a gap to see the pages compared and the evidence behind it.
              </div>
            </div>

            <div className="cs-gap-list">
              {gapsList.map((gap) => {
                const isOpen = openGaps[gap.id] ?? false;
                return (
                  <div key={gap.id} className="cs-card">
                    <div
                      className="cs-gap-summary"
                      role="button"
                      tabIndex={0}
                      onClick={() => toggleGap(gap.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          toggleGap(gap.id);
                        }
                      }}
                    >
                      <span className="cs-index">{gap.indexLabel}</span>
                      <div>
                        <p className="cs-gap-title">{gap.title}</p>
                        {gap.summary && (
                          <p className="cs-gap-issue">{gap.summary}</p>
                        )}
                      </div>
                      <div className="cs-summary-side">
                        <span className={`cs-severity ${gap.severity}`}>
                          {gap.severity === 'high'
                            ? 'High impact'
                            : gap.severity === 'medium'
                            ? 'Medium impact'
                            : 'Low impact'}
                        </span>
                        <span className={`cs-chevron ${isOpen ? 'open' : ''}`}>
                          ⌄
                        </span>
                      </div>
                    </div>

                    {isOpen && (
                      <div className="cs-gap-body">
                        <div className="cs-compare-grid">
                          <div className="cs-compare">
                            <div className="cs-compare-label">
                              {gap.yourSite.label}
                            </div>
                            {gap.yourSite.url && (
                              <a
                                className="cs-page-link"
                                href={
                                  gap.yourSite.url.startsWith('http')
                                    ? gap.yourSite.url
                                    : `https://${gap.yourSite.url}`
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {gap.yourSite.url}
                              </a>
                            )}
                            {gap.yourSite.description && (
                              <p>{gap.yourSite.description}</p>
                            )}
                          </div>
                          <div className="cs-compare competitor">
                            <div className="cs-compare-label">
                              {gap.competitor.label}
                            </div>
                            {gap.competitor.url && (
                              <a
                                className="cs-page-link"
                                href={
                                  gap.competitor.url.startsWith('http')
                                    ? gap.competitor.url
                                    : `https://${gap.competitor.url}`
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {gap.competitor.url}
                              </a>
                            )}
                            {gap.competitor.description && (
                              <p>{gap.competitor.description}</p>
                            )}
                          </div>
                        </div>

                        {gap.whyItMatters && (
                          <div className="cs-finding">
                            <b>Why it matters</b>
                            <span>{gap.whyItMatters}</span>
                          </div>
                        )}
                        {gap.response && (
                          <div className="cs-finding">
                            <b>Response</b>
                            <span>{gap.response}</span>
                          </div>
                        )}
                        {gap.addressedBy && (
                          <div className="cs-addressed">{gap.addressedBy}</div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Section 2: Content Opportunities */}
        <section id="opportunities" className="cs-section">
          <div className="cs-section-head">
            <div>
              <div className="cs-section-title">
                <h2>Content Opportunities</h2>
                <span className="cs-count">{opportunitiesList.length}</span>
              </div>
              <p className="cs-section-copy">
                The highest-value actions to close content gaps and improve your
                site.
              </p>
            </div>
            <div className="cs-section-note">
              Ordered by business relevance and expected content impact.
            </div>
          </div>

          {opportunitiesList.length === 0 ? (
            <div className="p-8 text-center bg-white rounded-xl border border-[#e3e3ed] text-[#68677c]">
              No content opportunities found for this site yet.
            </div>
          ) : (
            <div className="cs-opportunity-list">
              {opportunitiesList.map((opp) => {
                const isChangeOpen = openChanges[opp.id] ?? false;
                return (
                  <article
                    key={opp.id}
                    id={`opp-${opp.id}`}
                    className="cs-card cs-opportunity"
                  >
                    <span className="cs-index">{opp.indexLabel}</span>
                    <div>
                      <div className="cs-type-row">
                        <span
                          className={`cs-type ${
                            opp.type === 'update'
                              ? 'update'
                              : opp.type === 'blog'
                              ? 'blog'
                              : ''
                          }`}
                        >
                          {opp.typeLabel}
                        </span>
                        <span className="cs-impact">{opp.priority}</span>
                      </div>
                      <h3>{opp.title}</h3>
                      {opp.summary && <p>{opp.summary}</p>}
                      {opp.gapLinks.length > 0 && (
                        <div className="cs-gap-links">
                          {opp.gapLinks.map((gRef, gIdx) => (
                            <span key={gIdx} className="cs-gap-ref">
                              {gRef}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Actions according to type */}
                    {opp.type === 'new_page' && (
                      <button
                        className="cs-action"
                        type="button"
                        onClick={() => handleOpenPlanModal(opp)}
                        disabled={
                          isPlanLoading && selectedPlanOpp?.id === opp.id
                        }
                      >
                        {isPlanLoading && selectedPlanOpp?.id === opp.id
                          ? 'Generating...'
                          : opp.pagePlan ||
                            generatedPlans[opp.id] ||
                            generatedPlans[opp.rawItem?._id || '']
                          ? 'View page plan'
                          : 'Generate page plan'}
                      </button>
                    )}

                    {opp.type === 'update' && (
                      <>
                        <button
                          className="cs-action secondary"
                          type="button"
                          aria-expanded={isChangeOpen}
                          onClick={() => toggleChangePanel(opp.id)}
                        >
                          {isChangeOpen ? 'Hide changes' : 'View changes'}
                        </button>
                        <div
                          className={`cs-change-panel ${
                            isChangeOpen ? 'show' : ''
                          }`}
                        >
                          {opp.changes?.pageTitle && (
                            <div className="cs-change">
                              <b>Page title</b>
                              <span>{opp.changes.pageTitle}</span>
                            </div>
                          )}
                          {opp.changes?.h1 && (
                            <div className="cs-change">
                              <b>H1</b>
                              <span>{opp.changes.h1}</span>
                            </div>
                          )}
                          {opp.changes?.openingCopy && (
                            <div className="cs-change">
                              <b>Opening copy</b>
                              <span>{opp.changes.openingCopy}</span>
                            </div>
                          )}
                          {opp.changes?.newSection && (
                            <div className="cs-change">
                              <b>New section</b>
                              <span>{opp.changes.newSection}</span>
                            </div>
                          )}
                          {opp.changes?.servicePromise && (
                            <div className="cs-change">
                              <b>Service promise</b>
                              <span>{opp.changes.servicePromise}</span>
                            </div>
                          )}
                          {opp.changes?.proofSection && (
                            <div className="cs-change">
                              <b>Proof section</b>
                              <span>{opp.changes.proofSection}</span>
                            </div>
                          )}
                          {opp.changes?.callsToAction && (
                            <div className="cs-change">
                              <b>Calls to action</b>
                              <span>{opp.changes.callsToAction}</span>
                            </div>
                          )}
                        </div>
                      </>
                    )}

                    {opp.type === 'blog' && (
                      <button
                        className="cs-action"
                        type="button"
                        onClick={() => handleOpenBlogAgent(opp.title, opp)}
                      >
                        Open Blog Agent
                      </button>
                    )}
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </main>

      {/* AI Page Plan Modal Dialog */}
      <Dialog
        open={Boolean(isPlanModalOpen && selectedPlanOpp)}
        onClose={handleClosePlanModal}
        disableScrollLock
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            maxWidth: '760px',
            overflow: 'hidden'
          }
        }}
        aria-labelledby="planTitle"
      >
        {selectedPlanOpp && (
          <div className="cs-modal-card">
            {/* Modal Header */}
            <div className="cs-modal-head">
              <div>
                <div className="cs-eyebrow">AI page plan</div>
                <h3 id="planTitle">{selectedPlanOpp.title || 'Page plan'}</h3>
                <p id="planSubtitle">{selectedPlanOpp.summary || '-'}</p>
              </div>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
              >
                {/* {!isPlanLoading && currentPlan && (
                  <button
                    className="cs-modal-btn"
                    type="button"
                    onClick={handleRegeneratePlan}
                  >
                    <RefreshIcon sx={{ fontSize: 16 }} />
                    <span>Regenerate</span>
                  </button>
                )} */}
                <IconButton
                  size="small"
                  aria-label="Close page plan"
                  onClick={handleClosePlanModal}
                  sx={{
                    color: '#667085',
                    bgcolor: '#f1f1f6',
                    borderRadius: '8px',
                    width: 32,
                    height: 32,
                    '&:hover': { bgcolor: '#e2e2ec', color: '#1d1c2e' }
                  }}
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </div>
            </div>

            {/* Modal Body */}
            <div className="cs-modal-body">
              {isPlanLoading ? (
                <div className="cs-loading-state">
                  <div>
                    <div className="cs-spinner" />
                    <b>Building the page structure…</b>
                    <br />
                    Organising sections, SEO, visuals, and mobile guidance.
                  </div>
                </div>
              ) : currentPlan ? (
                <div>
                  {/* Plan Overview */}
                  <div className="cs-plan-overview">
                    <div className="cs-plan-box">
                      <b>Page goal</b>
                      <span>
                        {currentPlan.hero?.subheadline ||
                          (currentPlan as any).goal ||
                          (currentPlan as any).page_goal ||
                          selectedPlanOpp.summary ||
                          '-'}
                      </span>
                    </div>
                    <div className="cs-plan-box">
                      <b>Audience</b>
                      <span>{planAudience}</span>
                    </div>
                    <div className="cs-plan-box">
                      <b>Suggested URL</b>
                      <span>
                        {(currentPlan as any).suggested_url ||
                          (currentPlan as any).url ||
                          (selectedPlanOpp.targetKeyword
                            ? `/${selectedPlanOpp.targetKeyword
                                .toLowerCase()
                                .replace(/[^a-z0-9]+/g, '-')
                                .replace(/(^-|-$)/g, '')}`
                            : '-')}
                      </span>
                    </div>
                    <div className="cs-plan-box">
                      <b>Primary CTA</b>
                      <span>
                        {currentPlan.hero?.cta ||
                          (currentPlan as any).cta ||
                          (currentPlan as any).primary_cta ||
                          '-'}
                      </span>
                    </div>
                  </div>

                  {/* SEO Foundation */}
                  <div className="cs-plan-section">
                    <b>SEO foundation</b>
                    <div className="cs-plan-overview">
                      <div className="cs-plan-box">
                        <b>Title</b>
                        <span>
                          {currentPlan.seo?.meta_title ||
                            (currentPlan.seo as any)?.title ||
                            selectedPlanOpp.changes?.pageTitle ||
                            '-'}
                        </span>
                      </div>
                      <div className="cs-plan-box">
                        <b>H1</b>
                        <span>
                          {currentPlan.seo?.h1 ||
                            currentPlan.hero?.headline ||
                            selectedPlanOpp.changes?.h1 ||
                            selectedPlanOpp.title ||
                            '-'}
                        </span>
                      </div>
                      <div className="cs-plan-box">
                        <b>Meta description</b>
                        <span>{currentPlan.seo?.meta_description || '-'}</span>
                      </div>
                    </div>
                  </div>

                  {/* Page Structure (Wireframes) */}
                  <div className="cs-plan-section">
                    <b>Page structure</b>
                    <div className="cs-wireframe">
                      {currentPlan.sections &&
                      currentPlan.sections.length > 0 ? (
                        currentPlan.sections.map((sec: any, secIdx: number) => {
                          const sectionName =
                            sec.name || sec.title || `Section ${secIdx + 1}`;
                          const purpose = sec.purpose || '';
                          const keyPoints: string[] =
                            Array.isArray(sec.key_points) &&
                            sec.key_points.length > 0
                              ? sec.key_points
                              : Array.isArray(sec.bullets) &&
                                sec.bullets.length > 0
                              ? sec.bullets
                              : [];
                          const component = sec.component || '';
                          const visualGuidance =
                            sec.visual_guidance ||
                            sec.visual ||
                            sec.visual_notes ||
                            '';
                          const mobileBehavior =
                            sec.mobile_behavior ||
                            sec.mobile_behaviour ||
                            sec.mobile ||
                            sec.mobile_notes ||
                            '';
                          const hasMeta = Boolean(
                            component || visualGuidance || mobileBehavior
                          );

                          return (
                            <article
                              key={sec._id || secIdx}
                              className="cs-wire-row"
                              data-section-id={sec._id || ''}
                            >
                              <span className="cs-wire-num">{secIdx + 1}</span>
                              <div>
                                <h4>{sectionName}</h4>
                                {purpose && <p>{purpose}</p>}
                                {keyPoints.length > 0 && (
                                  <ul className="cs-key-points">
                                    {keyPoints.map((pt, pIdx) => (
                                      <li key={pIdx}>{pt}</li>
                                    ))}
                                  </ul>
                                )}
                                {hasMeta && (
                                  <div className="cs-section-meta">
                                    <div className="cs-section-detail">
                                      <b>Component</b>
                                      <span>{component || '-'}</span>
                                    </div>
                                    <div className="cs-section-detail">
                                      <b>Visual guidance</b>
                                      <span>{visualGuidance || '-'}</span>
                                    </div>
                                    <div className="cs-section-detail">
                                      <b>Mobile behaviour</b>
                                      <span>{mobileBehavior || '-'}</span>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </article>
                          );
                        })
                      ) : currentPlan.hero?.headline ||
                        currentPlan.hero?.subheadline ? (
                        <article className="cs-wire-row">
                          <span className="cs-wire-num">1</span>
                          <div>
                            <h4>
                              Hero:{' '}
                              {currentPlan.hero?.headline ||
                                selectedPlanOpp.title ||
                                '-'}
                            </h4>
                            <p>{currentPlan.hero?.subheadline || '-'}</p>
                            {currentPlan.hero?.visual_notes && (
                              <div className="cs-section-meta">
                                <div className="cs-section-detail">
                                  <b>Visual guidance</b>
                                  <span>{currentPlan.hero.visual_notes}</span>
                                </div>
                              </div>
                            )}
                          </div>
                        </article>
                      ) : (
                        <div className="cs-plan-box">
                          <span>-</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Site-wide Visual Guidance & Mobile Behaviour */}
                  <div className="cs-plan-section cs-guidance-grid">
                    <div className="cs-plan-box">
                      <b>Site-wide visual guidance</b>
                      {visualGuidanceList.length > 0 ? (
                        <ul>
                          {visualGuidanceList.map((item, idx) => (
                            <li key={idx}>{item}</li>
                          ))}
                        </ul>
                      ) : (
                        <span>-</span>
                      )}
                    </div>
                    <div className="cs-plan-box">
                      <b>Site-wide mobile behaviour</b>
                      {mobileBehaviourList.length > 0 ? (
                        <ul>
                          {mobileBehaviourList.map((item, idx) => (
                            <li key={idx}>{item}</li>
                          ))}
                        </ul>
                      ) : (
                        <span>-</span>
                      )}
                    </div>
                  </div>

                  {/* Dedicated Internal Links Section */}
                  <div className="cs-plan-section">
                    <b>Internal links</b>
                    {internalLinksList.length > 0 ? (
                      <div className="cs-internal-links">
                        {internalLinksList.map((link, idx) => (
                          <div key={idx} className="cs-internal-link">
                            <strong>{link.anchor}</strong>
                            <code>{link.target_url}</code>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div
                        className="cs-plan-box"
                        style={{ marginTop: '10px' }}
                      >
                        <span>-</span>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="p-6 text-center text-[#667085]">
                  Click &ldquo;Generate page plan&rdquo; to build the wireframe
                  plan for this page.
                </div>
              )}
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default ContentStrategyReport;
